import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '@/utils/api';
import api from '@/utils/api';
import { setToken, setUser } from '@/utils/auth';
import { toast } from 'sonner';
import { CheckCircle } from '@phosphor-icons/react';

/* PayGBot dark auth palette — mirrors landing + dashboard */
const AUTH_BG = '#0F1117';
const AUTH_PANEL = '#171A22';
const AUTH_CARD = '#1E222D';
const AUTH_BORDER = 'rgba(255,255,255,0.08)';

const INPUT_BASE =
  'w-full px-4 py-3 rounded-lg text-white placeholder-slate-500 border transition-all ' +
  'focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500/60';

const VALID_PLANS = ['starter', 'growth', 'pro'];

const Logo = ({ testId }) => (
  <Link to="/" className="inline-flex items-center space-x-2" data-testid={testId}>
    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/20">
      <span className="text-white font-bold text-xl">P</span>
    </div>
    <span className="text-2xl font-bold text-white tracking-tight">PayGBot</span>
  </Link>
);

const SignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [referralCode, setReferralCode] = useState('');
  // 2026-05-31 — P0 billing fix: public signups MUST route through
  // Stripe Checkout. The plan defaults to Starter when the user
  // arrives directly on /signup; pricing-page CTAs pass `?plan=...`.
  const initialPlan = (() => {
    const p = (searchParams.get('plan') || 'starter').toLowerCase();
    return VALID_PLANS.includes(p) ? p : 'starter';
  })();
  const [selectedPlan, setSelectedPlan] = useState(initialPlan);
  const [loading, setLoading] = useState(false);
  const [fromDemo, setFromDemo] = useState(false);

  useEffect(() => {
    // Check for referral code in URL
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
    }

    // Check if user came from demo
    const demoParam = searchParams.get('from') === 'demo';
    const demoStorage = localStorage.getItem('paygbot_from_demo') === 'true';
    setFromDemo(demoParam || demoStorage);

    // Pre-fill email from demo lead capture
    const demoLead = localStorage.getItem('paygbot_demo_lead');
    if (demoLead) {
      try {
        const leadData = JSON.parse(demoLead);
        setFormData(prev => ({
          ...prev,
          name: leadData.first_name || '',
          email: leadData.email || ''
        }));
        setFromDemo(true);
      } catch (e) {
        // Invalid JSON, ignore
      }
    }
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const registerData = {
        ...formData,
        referral_code: referralCode || null
      };
      const response = await authAPI.register(registerData);
      const { access_token, user, requires_checkout } = response.data;

      setToken(access_token);
      setUser(user);

      // Clear demo flags
      localStorage.removeItem('paygbot_from_demo');
      localStorage.removeItem('paygbot_demo_lead');

      // 2026-05-31 — P0 billing fix.
      // If the backend says payment is required, immediately create
      // a Stripe Checkout session for the selected plan and hand
      // window.location over to Stripe. Internal accounts return
      // requires_checkout=false and go straight to onboarding.
      if (requires_checkout) {
        try {
          const checkoutResp = await api.post('/create-checkout-session', {
            plan: selectedPlan,
          });
          const checkoutUrl = checkoutResp?.data?.checkout_url;
          if (!checkoutUrl) {
            throw new Error('No checkout URL returned');
          }
          toast.success('Redirecting to secure checkout…');
          // Stash the selected plan so /pricing knows what to clean
          // up if the user backs out of Stripe.
          localStorage.setItem('paygbot_pending_signup', '1');
          window.location.href = checkoutUrl;
          return;
        } catch (err) {
          toast.error(
            err?.response?.data?.detail
            || 'Could not start checkout. Please try again or contact support.'
          );
          setLoading(false);
          return;
        }
      }

      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12 relative overflow-hidden"
      style={{ backgroundColor: AUTH_BG }}
      data-testid="signup-page-shell"
    >
      {/* Ambient emerald glows */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #10b981, transparent)' }}
        />
        <div
          className="absolute top-1/2 -right-40 w-[560px] h-[560px] rounded-full opacity-10 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #34d399, transparent)' }}
        />
      </div>

      <div className="relative max-w-5xl w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Branding & Benefits */}
          <div className="hidden lg:block space-y-8">
            <div>
              <div className="mb-8">
                <Logo testId="signup-logo-desktop" />
              </div>

              {fromDemo && (
                <div
                  className="mb-6 p-4 rounded-xl border border-emerald-500/30"
                  style={{ backgroundColor: 'rgba(16,185,129,0.08)' }}
                >
                  <p className="text-emerald-200 font-medium">
                    You&apos;re one step away from unlocking the full PayGBot system.
                  </p>
                </div>
              )}

              <h2 className="text-3xl font-bold text-white mb-4 tracking-tight">
                Start Building Your Online Income
              </h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Join PayGBot and get instant access to all 4 bots designed for beginners.
              </p>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle size={24} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white">Access all 4 bots</div>
                    <p className="text-sm text-slate-400">Content, Money, and Business Builder — all unlocked</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle size={24} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white">Save your results</div>
                    <p className="text-sm text-slate-400">Keep all bot outputs in your project library</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle size={24} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white">Run unlimited projects</div>
                    <p className="text-sm text-slate-400">No limits on bot runs or saved projects</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle size={24} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-white">Built for beginners</div>
                    <p className="text-sm text-slate-400">Simple forms, clear outputs, no complexity</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Signup Form */}
          <div className="space-y-6">
            {/* Logo for mobile */}
            <div className="lg:hidden text-center">
              <Logo testId="signup-logo" />

              {fromDemo && (
                <div
                  className="mt-4 p-4 rounded-xl border border-emerald-500/30"
                  style={{ backgroundColor: 'rgba(16,185,129,0.08)' }}
                >
                  <p className="text-sm text-emerald-200 font-medium">
                    You&apos;re one step away from unlocking the full PayGBot system.
                  </p>
                </div>
              )}

              <h2 className="mt-6 text-3xl font-bold text-white tracking-tight">Create your account</h2>
              <p className="mt-2 text-sm text-slate-400">Start running bots that work for you</p>
            </div>

            <div className="hidden lg:block">
              {fromDemo && (
                <div
                  className="mb-4 p-4 rounded-xl border border-emerald-500/30"
                  style={{ backgroundColor: 'rgba(16,185,129,0.08)' }}
                >
                  <p className="text-emerald-200 font-medium text-center">
                    You&apos;re one step away from unlocking the full PayGBot system.
                  </p>
                </div>
              )}
              <h2 className="text-3xl font-bold text-white text-center tracking-tight">Create your account</h2>
              <p className="mt-2 text-slate-400 text-center">Get started in less than a minute</p>
            </div>

            {/* Form */}
            <form
              className="mt-8 space-y-6 p-8 rounded-2xl border shadow-2xl"
              style={{ backgroundColor: AUTH_CARD, borderColor: AUTH_BORDER }}
              onSubmit={handleSubmit}
              data-testid="signup-form"
            >
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-200 mb-2">
                    Full name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    data-testid="signup-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    className={INPUT_BASE}
                    style={{ backgroundColor: AUTH_PANEL, borderColor: AUTH_BORDER }}
                    placeholder="John Doe"
                    autoComplete="name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-200 mb-2">
                    Email address
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    data-testid="signup-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    className={INPUT_BASE}
                    style={{ backgroundColor: AUTH_PANEL, borderColor: AUTH_BORDER }}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-200 mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    minLength={6}
                    data-testid="signup-password-input"
                    value={formData.password}
                    onChange={handleChange}
                    className={INPUT_BASE}
                    style={{ backgroundColor: AUTH_PANEL, borderColor: AUTH_BORDER }}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {/* Phase G (2026-02-06) — V2 collapses the 3-tier signup
                  into the single Business Strategist plan. The picker
                  stays visually as a single card so the flow is
                  unchanged for users arriving from /pricing. */}
              <div data-testid="signup-plan-picker" className="space-y-2">
                <label className="block text-sm font-medium text-slate-200">
                  Your plan
                </label>
                <div
                  data-testid="signup-plan-business_strategist"
                  className="p-4 rounded-lg border border-emerald-500/60 ring-2 ring-emerald-500/25"
                  style={{ backgroundColor: 'rgba(16,185,129,0.10)' }}
                >
                  <div className="text-xs font-semibold text-emerald-200">Business Strategist</div>
                  <div className="text-2xl font-bold text-emerald-300 mt-1">
                    $49<span className="text-sm font-normal text-slate-400">/mo</span>
                  </div>
                  <ul className="text-xs text-slate-300 mt-2 space-y-0.5">
                    <li>• 30 monthly Business Credits</li>
                    <li>• Business Brain + Brand Brain + Business Kit</li>
                    <li>• Live Market + Opportunity Intelligence</li>
                    <li>• Every builder unlocked</li>
                  </ul>
                </div>
                <p className="text-xs text-slate-400" data-testid="signup-trial-copy">
                  7-day free trial, then $49/month unless canceled. Buy Business Credit packs any time.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="signup-submit-button"
                className="w-full py-3 px-4 bg-gradient-to-br from-emerald-500 to-green-600 hover:shadow-lg hover:shadow-emerald-500/25 text-white font-medium rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                {loading ? 'Creating account…' : 'Continue to secure checkout →'}
              </button>

              <div className="text-center text-sm">
                <span className="text-slate-400">Already have an account? </span>
                <Link
                  to="/login"
                  data-testid="signup-login-link"
                  className="font-medium text-emerald-400 hover:text-emerald-300 transition-colors focus:outline-none focus:underline"
                >
                  Sign in
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
